import { type FunctionComponent, type PropsWithChildren, type ReactElement, useEffect, useState } from 'react';
import { type UseFormRegister } from 'react-hook-form';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';

const ELEMENTS_FROM_BOTTOM_BUFFER = 3;

export type RadioOptionProps =
	| {
			id?: string;
			name: string;
			value: string;
			description?: string | ReactElement;
			disabled?: boolean;
			optionClassName?: string;
	  }
	| {
			id: string;
			name: string | ReactElement;
			value: string;
			description?: string | ReactElement;
			disabled?: boolean;
			optionClassName?: string;
	  };

export type RadioButtonGroupProps = {
	value?: string;
	groupName: string;
	options: RadioOptionProps[];
	defaultValue?: string;
	defaultSelected?: boolean; // True by default, setting to false will make no radio selected initially
	bold?: boolean;
	btnPadding?: boolean;
	alignItems?: 'start' | 'center' | 'end';
	textClassName?: string;
	onSelectionChanged?: (selectedOption: string) => void;
	automationHook?: string;
	orientation?: 'horizontal' | 'vertical' | 'responsive';
	className?: string;
	register?: UseFormRegister<any>;
	loadMoreRef?: React.Ref<HTMLDivElement>;
};

export const RadioButtonGroup: FunctionComponent<PropsWithChildren<RadioButtonGroupProps>> = ({
	value,
	groupName,
	options,
	defaultValue,
	defaultSelected = true,
	onSelectionChanged,
	bold,
	btnPadding,
	alignItems = 'start',
	textClassName = 'flex flex-column',
	automationHook,
	children,
	orientation = 'vertical',
	className = '',
	register,
	loadMoreRef
}) => {
	const isControlled = Boolean(value);
	const [internalSelected, setInternalSelected] = useState<string | null>(defaultSelected ? defaultValue || options[0].value : null);
	const selectedValue = isControlled ? value : internalSelected;

	const onChange = (newValue: string) => {
		if (!isControlled) {
			setInternalSelected(newValue);
		}
		if (onSelectionChanged) {
			onSelectionChanged(newValue);
		}
	};

	useEffect(() => {
		if (!isControlled && defaultSelected && !options.find((option) => option.value === selectedValue)) {
			setInternalSelected(options[0].value);
		}
	}, [options]);

	return (
		<div
			className={`flex ${
				orientation === 'responsive' ? 'flex-row-ns flex-column' : orientation === 'horizontal' ? 'flex-row' : 'flex-column'
			}`}>
			{options.map(({ id, name, value: optionValue, description, disabled = false, optionClassName }, index) => (
				<div ref={index === options.length - ELEMENTS_FROM_BOTTOM_BUFFER ? loadMoreRef : undefined} key={`${id}-${index}`}>
					<label
						className={`flex pointer ${children ? 'flex-column' : 'items-center'} ${
							orientation === 'responsive' ? ' mr3-ns' : 'mb1'
						} ${selectedValue === name ? '' : optionClassName}`}
						htmlFor={typeof name === 'string' ? name : id}
						data-automation={generateDataSelector('radiogroup', automationHook)}
						data-testid={`${name}-${id}`}>
						<span className={`flex ${className} items-${alignItems} ${btnPadding ? 'pv2' : ''}`}>
							<input
								id={typeof name === 'string' ? name : id}
								type={'radio'}
								name={groupName}
								value={optionValue}
								disabled={disabled}
								checked={selectedValue === optionValue}
								className={'ma0 pointer'}
								onChange={(e) => onChange(e.target.value)}
								style={{ marginTop: 2 }}
								{...register}
							/>
							<span className={`${textClassName} ml2 ${bold ? 'font-medium' : ''}`}>
								{name}
								{description && <span className={'theme-grey f6'}>{description}</span>}
							</span>
						</span>
						{selectedValue === optionValue && children ? <span className={'pa3'}>{children}</span> : null}
					</label>
				</div>
			))}
		</div>
	);
};
